/**
 * Per-browser display preferences: light, dark or the system's choice, and a
 * media-query hook for the drawer. Preferences, not state: a browser that
 * refuses storage simply gets the defaults.
 */
import { useCallback, useEffect, useState } from 'react'

export type ThemeMode = 'light' | 'dark' | 'system'
const THEME_KEY = 'cms.theme'

export function readThemeMode(): ThemeMode {
  try {
    const stored = window.localStorage.getItem(THEME_KEY)
    return stored === 'light' || stored === 'dark' ? stored : 'system'
  } catch {
    return 'system'
  }
}

/** `data-theme` only when the person chose; without it the CSS follows `prefers-color-scheme`. */
export function applyThemeMode(mode: ThemeMode): void {
  if (mode === 'system') delete document.documentElement.dataset.theme
  else document.documentElement.dataset.theme = mode
}

export function useThemeMode(): [ThemeMode, (mode: ThemeMode) => void] {
  const [mode, setModeState] = useState<ThemeMode>(readThemeMode)
  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next)
    applyThemeMode(next)
    try {
      if (next === 'system') window.localStorage.removeItem(THEME_KEY)
      else window.localStorage.setItem(THEME_KEY, next)
    } catch {
      // Not saved: the choice holds for this visit only.
    }
  }, [])
  return [mode, setMode]
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches)
  useEffect(() => {
    const list = window.matchMedia(query)
    const update = () => setMatches(list.matches)
    update()
    list.addEventListener('change', update)
    return () => list.removeEventListener('change', update)
  }, [query])
  return matches
}

const DRAWER_KEY = 'cms.drawer'

export function readRailPreference(): boolean {
  try {
    return window.localStorage.getItem(DRAWER_KEY) === 'rail'
  } catch {
    return false
  }
}

export function saveRailPreference(rail: boolean): void {
  try {
    if (rail) window.localStorage.setItem(DRAWER_KEY, 'rail')
    else window.localStorage.removeItem(DRAWER_KEY)
  } catch {
    // Preference only.
  }
}
